import {
	addNewRelicPageAction,
	setNewRelicCustomAttribute
} from 'ddc-new-relic';
import { hysteriFetch, wrappers } from 'hysterics';
import {
	urlizeFacetObj,
	getUrlParams,
	filterNonInventoryParams,
	findSBParamsMappingKey
} from '../utilities/url-params';
import { setDataLayerTracking } from '../utilities/tracking';
import { sendPixallData } from '../utilities/pixall';
import { logOnServer } from '../utilities/fetch-endpoint';
import { geoLocationObj } from '../global-constants';

// dependency of ws-scripts
// TODO: share from ws-scripts or a util
const uuidv4 = require('uuid/v4');

export class InvData {
	constructor(flags) {
		this.initialLoad = true;

		// Feature flags
		this.flags = flags;

		// User selections
		this.start = 0;
		this.appliedFilters = [];

		// url params
		this.urlParams = {};

		// Data returned from services
		this.facets = {};
		this.inventory = {};
		this.sortBy = '';
		this.filterType = '';
		this.filterSearchEventData = {};

		this.SRP_SESSION_ID_KEY = 'srpSessionId';
		this.SRP_REQUEST_ID_KEY = 'srpRequestId';
		this.TOPIC_PREFIX = 'ws-inv-data';
		this.FACETS_TOPIC = `${this.TOPIC_PREFIX}/facets`;
		this.INVENTORY_TOPIC = `${this.TOPIC_PREFIX}/inventory`;
		this.SORT_BY_TOPIC = `${this.TOPIC_PREFIX}/sortBy`;
		this.TOGGLE_LISTING_VIEW_TOPIC = `${this.TOPIC_PREFIX}/toggleListingView`;
		this.WINDOW_ID = 'inventory-data-bus1';
		this.FACETS_API_URL =
			window.DDC.WidgetData[this.WINDOW_ID].props.facetApiURL;
		this.INVENTORY_API_URL =
			window.DDC.WidgetData[this.WINDOW_ID].props.inventoryApiURL;
		this.SEARCH_API_URL =
			window.DDC.WidgetData[this.WINDOW_ID].props.searchApiURL;
		this.COMPARE_PAGE =
			window.DDC.WidgetData[this.WINDOW_ID].props.comparePage;

		// Start temp code
		// This is temp code to track issues on SRP
		// set a custom tracking id for session & request in LD
		this.SRP_SESSION_ID =
			this.flags['ws-inv-data-custom-session-id'] !== 'false'
				? this.flags['ws-inv-data-custom-session-id']
				: window.DDC.WidgetData[this.WINDOW_ID].props.srpSessionId;
		this.SRP_REQUEST_ID =
			this.flags['ws-inv-data-custom-request-id'] !== 'false'
				? this.flags['ws-inv-data-custom-request-id']
				: window.DDC.WidgetData[this.WINDOW_ID].props
						.initialSrpRequestId;
		// End temp code

		this.DEBOUNCE_WAIT = 100;
		this.PAGE_ID = window.DDC.WidgetData[this.WINDOW_ID].props.pageId;
		this.PAGE_LABEL = window.DDC.WidgetData[this.WINDOW_ID].props.pageLabel;

		this.useEnhancedLogging = this.flags['use-srp-enhanced-logging'];

		if (window.DDC.WidgetData[this.WINDOW_ID].props.geoData) {
			this.geoData = window.DDC.WidgetData[this.WINDOW_ID].props.geoData;
			this.geoDataPrefs =
				window.DDC.WidgetData[this.WINDOW_ID].props.geoDataPrefs;
		}

		this.fetchers = {
			inventory: hysteriFetch('ws-inv-data-inventory-fetch'),
			facets: hysteriFetch('ws-inv-data-facets-fetch')
		};

		this._requestResource = async (resourceType, eventType) => {
			const resourceBase = resourceType.toUpperCase();
			const queryString = this.getQueryString();
			const paramSeparator = queryString.includes('?') ? '&' : '?';
			const tracingParams = `${paramSeparator}${this.SRP_SESSION_ID_KEY}=${this.SRP_SESSION_ID}&${this.SRP_REQUEST_ID_KEY}=${this.SRP_REQUEST_ID}`;
			const resourceUrl = `${
				this[`${resourceBase}_API_URL`]
			}${queryString}${tracingParams}`;

			const resourceTopic = `${resourceBase}_TOPIC`;

			const startTime = Date.now();
			const responseHeaders = [];
			let response;

			this.publishEvent(`${this[resourceTopic]}-request`);

			try {
				if (!this.fetchers[resourceType]) {
					throw Error(
						`Cannot find fetch function for resource type: ${resourceType}`
					);
				}

				// This contains the call's raw Response which is used to get the response headers
				response = await this.fetchers[resourceType](
					resourceUrl,
					{
						method: 'GET',
						credentials: 'include',
						mode: 'no-cors'
					},
					{
						[wrappers.retry.contextKey]: {
							retries: this.flags['ws-inv-data-fetch-retries']
						},
						[wrappers.timeout.contextKey]: {
							timeout: this.flags['ws-inv-data-fetch-timeout']
						},
						[wrappers.fetch.contextKey]: {
							responseBodyType: null,
							validateResponse: null
						}
					}
				);

				response.headers.forEach((value, key) =>
					responseHeaders.push({ key, value })
				);

				// get the body of the Response
				const responseBody = await response.json().then(data => {
					return data;
				});

				if (resourceType === 'facets') {
					if (this.geoData) {
						responseBody.facets.unshift(geoLocationObj);
					}

					this.appliedFilters = responseBody.filters || [];
					this[resourceType] = responseBody;
				}
				if (resourceType === 'inventory') {
					// Nested destructuring of responseBody is used to isolate trackingData because it is only used in
					// datalayer and should not be passed to other widgets or written to InvData
					const {
						pageInfo: { trackingData, ...pageInfo },
						...resourceObject
					} = responseBody;
					this[resourceType] = { pageInfo, ...resourceObject };

					setDataLayerTracking(
						this.appliedFilters,
						this.sortBy,
						{
							pageInfo,
							trackingData
						},
						eventType,
						this.filterSearchEventData,
						this.flags['toggle-srp-filtersearch-event-publish']
					);

					// Do not push to pixall on initial load because it would duplicate pixall pageload event
					if (!this.initialLoad) {
						sendPixallData(
							this.appliedFilters,
							this.PAGE_ID,
							this.PAGE_LABEL
						);
						// Temp code: use FF in LD to track request with custom id
						this.SRP_REQUEST_ID =
							this.flags['ws-inv-data-custom-request-id'] !==
							'false'
								? this.flags['ws-inv-data-custom-request-id']
								: uuidv4();
					} else {
						this.initialLoad = false;
					}

					// TEMP CODE (for matching numbers on the "Please Call"/no pricing issue)
					let totalPricingCount = 0;

					// Is this a case where all vehicles have empty pricing objects?
					{
						const SRP_NO_PRICING_LABEL = `${this.TOPIC_PREFIX}-no-pricing-warning`;
						const SRP_NO_PRICING_TEXT =
							'pricing was not found for any vehicle in data';
						const { inventory = [] } = resourceObject;
						inventory.forEach(vehicle => {
							if (
								vehicle.pricing &&
								vehicle.pricing.dPrice &&
								vehicle.pricing.dPrice.length
							) {
								totalPricingCount +=
									vehicle.pricing.dPrice.length;
							}
						});
						if (totalPricingCount === 0) {
							// add attribute to NR transaction
							setNewRelicCustomAttribute(
								SRP_NO_PRICING_LABEL,
								SRP_NO_PRICING_TEXT
							);
							// log on server
							logOnServer({
								logLabel: SRP_NO_PRICING_LABEL,
								srpSessionId: this.SRP_SESSION_ID,
								srpRequestId: this.SRP_REQUEST_ID,
								errorMsg: SRP_NO_PRICING_TEXT
							});
						}
					}
				}
				const requestDuration = Date.now() - startTime;

				this.publishEvent(this[resourceTopic], [
					{
						timingData: {
							requestDuration,
							initialStartTime: startTime
						}
					}
				]);
			} catch (_e) {
				this.publishEvent(`${this.TOPIC_PREFIX}/request-failed`);
				const errorMsg = _e.toString();
				const errorLogLabel = `WS INV DATA/GET ${resourceBase}/ERROR`;

				// send the request info to endpoint to log error on server
				// we don't need a response here
				logOnServer({
					logLabel: errorLogLabel,
					srpSessionId: this.SRP_SESSION_ID,
					srpRequestId: this.SRP_REQUEST_ID,
					responseHeaders: JSON.stringify(responseHeaders),
					responseUrl: response.url,
					errorMsg
				});

				addNewRelicPageAction('WS INV DATA REQUEST FAILED', {
					resourceType,
					errorMsg
				});

				setNewRelicCustomAttribute(
					`${this.TOPIC_PREFIX}-request-failure`,
					'true'
				);

				const sessionErrorLabel = `${errorLogLabel}/SRP SESSION ID`;
				const requestErrorLabel = `${errorLogLabel}/SRP REQUEST ID`;
				setNewRelicCustomAttribute(
					sessionErrorLabel,
					this.SRP_SESSION_ID
				);
				setNewRelicCustomAttribute(
					requestErrorLabel,
					this.SRP_REQUEST_ID
				);
			}
		};

		// Debounced request functions
		this._requestFacets = window._.debounce(() => {
			this._requestResource('facets');
		}, this.DEBOUNCE_WAIT);

		this._requestInventory = window._.debounce(eventType => {
			this._requestResource('inventory', eventType);
		}, this.DEBOUNCE_WAIT);

		this._publishSortBy = window._.debounce(() => {
			this.publishEvent(this.SORT_BY_TOPIC, this.sortBy);
		}, this.DEBOUNCE_WAIT);

		this._loadInitialData();
	}

	registerFacetNotification(callback) {
		this.subscribeEvent(this.FACETS_TOPIC, callback);
	}

	registerInventoryNotification(callback) {
		this.subscribeEvent(this.INVENTORY_TOPIC, callback);
	}

	registerSortByNotification(callback) {
		this.subscribeEvent(this.SORT_BY_TOPIC, callback);
	}

	registerToggleListingView(callback) {
		this.subscribeEvent(this.TOGGLE_LISTING_VIEW_TOPIC, callback);
	}

	getFacets() {
		return this.facets;
	}

	getInventory() {
		return this.inventory;
	}

	getPaging() {
		return this.inventory.pageInfo;
	}

	getSortBy() {
		return this.sortBy;
	}

	getAppliedFilters() {
		return this.appliedFilters;
	}

	getComparePage() {
		return this.COMPARE_PAGE;
	}

	getGeoData() {
		return this.geoData;
	}

	getGeoDataPrefs() {
		return this.geoDataPrefs;
	}

	replaceSelections(urlParams, sortBy, start, updateBrowserHistory = true) {
		this.urlParams = urlParams;
		this.start = start;
		this.sortBy = sortBy;

		if (updateBrowserHistory) {
			this._updateBrowserHistory();
		}

		this._requestFacets();
		this._requestInventory();
	}

	clearFilters() {
		const urlParams = filterNonInventoryParams(
			this.urlParams,
			this.appliedFilters
		);

		this.replaceSelections(urlParams, this.sortBy, 0 /* start */, true);
	}

	setSortBy(value = '') {
		this.sortBy = value.split();

		this._updateBrowserHistory();
		this._requestInventory('sort');
		this._publishSortBy();
	}

	/**
	 *
	 * @param {string} filterName Name of filter (ex: 'make')
	 * @param {string[]} filterValues Values of filter (ex: ['BMW', 'Toyota'])
	 * @param {string} filterValue most recently selected value of the filter, used for tracking
	 * @param {boolean} [checked=true] if filter is being added (true) or removed (false)
	 * @param {boolean} [isSlider=false] if filter is slider
	 * @param {boolean} [isBreadcrumb=false] if filter is breadcrumb
	 * @param element {object} element interacted with
	 */
	setFilterSelections({
		filterName,
		filterValues,
		filterValue,
		element,
		isSelected = true,
		isSlider = false,
		isChip = false
	}) {
		// find a SB param key if it exists to add or remove facet
		const SBParamKey = findSBParamsMappingKey(filterName);
		const hasLegacyParam =
			SBParamKey !== [] &&
			Object.prototype.hasOwnProperty.call(this.urlParams, SBParamKey);

		if (!filterValues || !filterValues.length) {
			// Handle removing deprecated legacy `incentiveId` param due to conditional applied filter logic for `incentiveIds`
			if (
				filterName === 'incentiveIds' &&
				!this.urlParams.incentiveIds &&
				this.urlParams.incentiveId
			) {
				delete this.urlParams.incentiveId;
			} else {
				// Handle removing SB param from the url
				if (hasLegacyParam) {
					delete this.urlParams[SBParamKey];
				}
				delete this.urlParams[filterName];
			}
		} else {
			// Handle removing only SB param from the url with other facet choices selected
			if (
				hasLegacyParam &&
				!filterValues.includes(this.urlParams[SBParamKey][0])
			) {
				delete this.urlParams[SBParamKey];
			}
			this.urlParams[filterName] = filterValues;
		}
		this.start = 0;
		this.filterType = filterName;
		this.filterSearchEventData = {
			clickedFacetName: filterName,
			clickedFacetValue: filterValue,
			checked: isSelected,
			isSlider,
			isBreadcrumb: isChip,
			element
		};

		// setting DDC.postalCode cookie when user changes zip code
		if (filterName === 'geoZip') {
			document.cookie = `DDC.postalCode=${filterValue}; path=/`;
			this.geoData.geoZip = filterValue;
		}

		this._updateBrowserHistory();
		this._requestFacets();
		this._requestInventory('facet');
	}

	setStart(index, updateBrowserHistory = true) {
		this.start = index.toString().split();

		if (updateBrowserHistory) {
			this._updateBrowserHistory();
		}

		this._requestFacets();
		this._requestInventory();
	}

	_updateBrowserHistory() {
		window.history.pushState(
			{
				urlParams: this.urlParams,
				sortBy: this.sortBy,
				start: this.start
			},
			'',
			`${window.location.origin}${
				window.location.pathname
			}${this.getQueryString()}`
		);
	}

	getQueryStringObj(overrides = {}) {
		const urlParams = { ...this.urlParams };

		if (this.sortBy && !this.sortBy.includes('')) {
			urlParams.sortBy = this.sortBy;
		}
		if (this.start !== 0) {
			urlParams.start = this.start;
		}

		return {
			...urlParams,
			...overrides
		};
	}

	getQueryString(overrides = {}) {
		return urlizeFacetObj(this.getQueryStringObj(overrides));
	}

	/* eslint-disable class-methods-use-this */
	publishEvent(topic, ...params) {
		if (window && window.DDC && window.DDC.pubsub) {
			window.DDC.pubsub.publish(topic, ...params);
		}
	}

	subscribeEvent(topic, callback) {
		if (window && window.DDC && window.DDC.pubsub) {
			window.DDC.pubsub.subscribe(topic, callback);
		}
	}
	/* eslint-enable class-methods-use-this */

	_loadInitialData() {
		setNewRelicCustomAttribute('isSRP', 'true');
		const { sortBy = '', start = 0, ...urlParams } = getUrlParams(
			`?${window.DDC.WidgetData[this.WINDOW_ID].props.params}`
		);

		const hasGeoParams = this.geoData
			? Object.keys(this.geoData).every(key =>
					Object.keys(urlParams).includes(key)
			  )
			: null;
		if (this.geoData && !hasGeoParams) {
			this.urlParams = { ...urlParams, ...this.geoData };

			// Populating URL params on page load
			window.history.pushState(
				{
					urlParams: this.urlParams
				},
				'',
				`${window.location.origin}${
					window.location.pathname
				}${this.getQueryString()}`
			);
		} else {
			this.urlParams = urlParams;
		}

		this.sortBy = sortBy;
		this.start = start;

		this._requestFacets();
		this._requestInventory();
		this._publishSortBy();
	}
}
